import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * One-time / subscribe toggle for Starter Pack PDPs.
 *
 * Starter Packs are Cart Transform bundles and cannot carry a selling plan
 * (Shopify rejects an expand on a line that has one), so "subscribe" adds two
 * lines in a single request: the pack one-time, plus the paired "Firefly Refill
 * Plan" placeholder carrying the plan. The firefly-tiered-pricing Function
 * zeroes the placeholder on the first order, but only while it pairs 1:1 with a
 * matching pack — ct-refill-plan-guard.js keeps that true afterwards.
 *
 * Submission is intercepted at the WINDOW capture phase. The theme registers
 * its declarative `on:submit` handler on `document` (see component.js), and
 * capture runs window -> document, so a window-capture listener is the only
 * hook that can pre-empt it without mutating theme markup. One-time purchases
 * fall straight through to the theme's own handler untouched.
 */

/**
 * `percent`, `today` and `save` are the subscriber markdown as
 * firefly-tiered-pricing will apply it at checkout, recomputed in Liquid from
 * the live plan. They are ABSENT (percent 0, empty strings) when Liquid could
 * not verify the plan against the Function's own guard, which is the signal to
 * quote the plain price and show no savings claim at all.
 * @typedef {{id: number, name: string, cadence?: string, starts?: string, price: string, percent?: number, today?: string, save?: string}} Plan
 */
/**
 * `pairs` is what the selected pack ships, `devices` the same count in single
 * units. Both are 0 when the variant map's third column is absent, which is the
 * signal to drop any copy that quotes a quantity.
 * @typedef {{id: number, title: string, available: boolean, was?: string, pairs?: number, devices?: number, plans: Plan[]}} Pair
 */

const root = () => window.Shopify?.routes?.root || '/';

/**
 * Line item property linking a pack line to its placeholder. Underscore
 * prefixed so checkout and the cart never print it.
 *
 * It is deliberately NOT what the Function pairs on — a line property is
 * buyer-supplied, and firefly-tiered-pricing keeps pairing by variant and
 * parentRelationship, which cannot be forged. This token only drives
 * ct-refill-plan-guard, which runs in the browser and is not a security
 * boundary. Both files must agree on this key.
 */
export const PAIR_KEY = '_ct_sub';

/**
 * Query-string test overrides, used by the ?view=subscription-demo template.
 *
 *   ?plan=<id>    preselect one subscription plan, so a specific Recharge plan
 *                 can be exercised without changing which plans are attached to
 *                 the placeholder product.
 *   ?ct_debug=1   log every plan the placeholder variants carry.
 *
 * IMPORTANT: ?plan= takes a SHOPIFY selling plan id — the id in
 * selling_plan_allocations, what /cart/add.js posts, and what the Function
 * reads back. A Recharge plan id is a DIFFERENT number and never reaches the
 * storefront, so ?ct_debug=1 is how you find the Shopify id for a given
 * Recharge plan.
 */
const param = (/** @type {string} */ name) => new URLSearchParams(window.location.search).get(name) || '';

/** Rebuy Smart Cart renders its own drawer and ignores theme events. */
function refreshSmartCart() {
  const smartCart = /** @type {any} */ (window).Rebuy?.SmartCart;
  for (const method of ['refresh', 'reloadCart', 'update']) {
    if (typeof smartCart?.[method] === 'function') {
      smartCart[method]();
      return;
    }
  }
}

class CtRefillPlanToggle extends HTMLElement {
  /** @type {Record<string, Pair>} */
  #pairs = {};

  /** @type {string} */
  #packVariantId = '';

  /** Chosen delivery frequency. Empty until the shopper picks one, which is
   *  the signal to fall back to the first plan in allowlist order. */
  #planIdValue = '';

  /** @type {HTMLFormElement | null} */
  #cachedForm = null;

  #abort = new AbortController();

  connectedCallback() {
    try {
      this.#pairs = JSON.parse(this.querySelector('[data-refill-plans]')?.textContent || '{}');
    } catch {
      this.#pairs = {};
    }
    for (const entry of Object.values(this.#pairs)) entry.plans = this.#ordered(entry.plans ?? []);

    const { signal } = this.#abort;

    this.#planIdValue = param('plan');

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });

    // The pack variant is chosen by ct-bundle-picker, which drives the theme's
    // <variant-picker>; it announces the new variant on the section.
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });

    this.#keepVariantPickerOnThisTemplate();

    this.#packVariantId = this.#form?.querySelector('input[name="id"]')?.value || '';
    this.#render();

    if (param('ct_debug')) console.info('[ct-refill-plan-toggle] pairs', this.#pairs);
  }

  /**
   * Keep <variant-picker>'s background fetch on the CURRENT template.
   *
   * The picker rebuilds variant state by re-fetching the product page from
   * `data-product-url`, which has no `?view=` suffix. On an alternate template
   * that returns the DEFAULT template's HTML — and Shopify prefixes block ids
   * with a per-template hash, so `updateVariantPicker` finds no matching picker,
   * throws "No new variant picker source found", and never dispatches
   * variant:update. The visible symptom is that choosing a different pack
   * updates the URL but not the add-to-cart form, so the wrong variant is
   * bought. Re-attaching `view` keeps the response on this template.
   *
   * This is a theme-wide limitation of `?view=` product templates, not
   * something specific to this block; the patch is scoped to the demo because
   * that is the only place it currently bites.
   */
  #keepVariantPickerOnThisTemplate() {
    const view = new URLSearchParams(window.location.search).get('view');
    if (!view) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    customElements.whenDefined('variant-picker').then(() => {
      if (this.#abort.signal.aborted) return;
      for (const picker of section.querySelectorAll('variant-picker')) {
        if (picker.dataset.ctViewPatched) continue;
        const build = picker.buildRequestUrl;
        if (typeof build !== 'function') continue;
        picker.dataset.ctViewPatched = '1';
        picker.buildRequestUrl = (...args) => {
          const url = build.apply(picker, args);
          return url.includes('view=') ? url : `${url}&view=${encodeURIComponent(view)}`;
        };
      }
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  // ---------------------------------------------------------------------- //

  /** Resolved lazily: the buy buttons render after this block in the section. */
  get #form() {
    if (this.#cachedForm?.isConnected) return this.#cachedForm;
    this.#cachedForm = /** @type {HTMLFormElement | null} */ (
      this.closest('.shopify-section')?.querySelector('product-form-component form[data-type="add-to-cart-form"]') ??
        null
    );
    return this.#cachedForm;
  }

  /** @returns {Pair | undefined} */
  get #entry() {
    return this.#pairs[this.#packVariantId];
  }

  get #subscribed() {
    return this.querySelector('[data-refill-mode]:checked')?.getAttribute('value') === 'subscribe';
  }

  /**
   * Plans in the block's allowlist order (data-plan-order), so the first id
   * listed is the default. Ids not in the list keep their own order after it.
   * @param {Plan[]} plans
   */
  #ordered(plans) {
    const order = (this.dataset.planOrder || '').split(',').filter(Boolean);
    if (!order.length) return plans;
    const rank = (/** @type {Plan} */ plan) => {
      const index = order.indexOf(String(plan.id));
      return index === -1 ? order.length : index;
    };
    return [...plans].sort((a, b) => rank(a) - rank(b));
  }

  /**
   * Fills the settings token set from a plan. Every token resolves to a live
   * value or to an empty string; a template that asks for a figure this plan
   * could not verify ({percent}, {save}, {today}) is suppressed by the caller
   * rather than printed with a hole in it.
   * @param {string} template
   * @param {Plan} plan
   */
  #fill(template, plan) {
    /** @type {Record<string, string>} */
    const values = {
      price: plan.price ?? '',
      cadence: this.#cadence(plan),
      starts: this.#starts(plan),
      percent: plan.percent ? String(plan.percent) : '',
      save: plan.save ?? '',
      today: plan.today ?? '',
      was: this.#entry?.was ?? '',
      pairs: this.#count(this.#entry?.pairs, 'pair'),
      devices: this.#count(this.#entry?.devices, 'device'),
    };
    return template.replace(
      /\{(price|cadence|starts|percent|save|today|was|pairs|devices)\}/g,
      (_, key) => values[key] ?? ''
    );
  }

  /**
   * "2 pairs", "1 pair", or empty when the count is unknown.
   * @param {number | undefined} value
   * @param {string} noun
   */
  #count(value, noun) {
    if (!value) return '';
    return `${value} ${noun}${value === 1 ? '' : 's'}`;
  }

  /** Whether `plan` carries a markdown Liquid was able to verify. */
  #discounted(plan) {
    return !!plan && !!plan.percent && !!plan.today;
  }

  /**
   * Rewrites an element's text from the token template stashed in `data-tpl`,
   * and reports whether the result is fit to show.
   *
   * A template that quotes a figure the Function could not be shown to honour
   * ({percent}, {save}, {today}) is REFUSED rather than filled: those tokens
   * resolve to an empty string, which would leave the line reading "% off every
   * refill" — a broken sentence making a claim we cannot stand behind. Callers
   * hide what this returns false for.
   * @param {Element | null} element
   * @param {Plan | undefined} plan
   * @returns {boolean} whether the element now holds text worth showing
   */
  #fillTpl(element, plan) {
    if (!(element instanceof HTMLElement) || element.dataset.tpl == null || !plan) return false;
    const template = element.dataset.tpl;
    if (/\{(percent|save|today)\}/.test(template) && !this.#discounted(plan)) return false;
    if (/\{(pairs|devices)\}/.test(template) && !this.#entry?.pairs) return false;
    element.textContent = this.#fill(template, plan);
    return !!element.textContent.trim();
  }

  /**
   * Button label for a delivery frequency: "Every 2 months". Built from the
   * plan's cadence rather than its name, because the Recharge plan names are
   * merchandising strings ("Refills Every Two Months - 15% Off") that repeat
   * the discount and do not agree with each other on separators or numerals.
   * @param {Plan} plan
   */
  #freqLabel(plan) {
    const cadence = this.#cadence(plan);
    return cadence ? cadence.charAt(0).toUpperCase() + cadence.slice(1) : plan.name;
  }

  /**
   * When the first renewal lands: "next month", "in 2 months". Liquid derives
   * it from the plan's frequency option; failing that, from the cadence.
   * @param {Plan} plan
   */
  #starts(plan) {
    if (plan.starts) return plan.starts;
    const match = this.#cadence(plan).match(/^every\s+(.+)$/i);
    if (!match) return '';
    return /^\d/.test(match[1]) ? `in ${match[1]}` : `next ${match[1]}`;
  }

  /**
   * Visible line item property for the placeholder line. Checkout prints
   * properties under the line item, which is the only spot in checkout where
   * we can state that nothing ships today; the product title, variant title,
   * plan name, and Shopify's "First payment $0.00, then …" line are all
   * either data or generated. A leading underscore would hide it, so the
   * label must not start with one.
   * @param {Plan | undefined} plan
   * @returns {Record<string, string> | null}
   */
  #lineNote(plan) {
    const label = (this.dataset.lineNoteLabel || '').trim();
    const note = (this.dataset.lineNote || '').trim();
    if (!label || !note) return null;
    return { [label]: plan ? this.#fill(note, plan) : note };
  }

  get #planId() {
    const plans = this.#entry?.plans ?? [];
    // A pinned ?plan= or an earlier choice only stands while it is actually
    // allocated to THIS placeholder variant; switching packs otherwise posts a
    // selling_plan Shopify will reject.
    if (plans.some((plan) => String(plan.id) === this.#planIdValue)) return this.#planIdValue;
    return plans[0] ? String(plans[0].id) : '';
  }

  /** @returns {Plan | undefined} */
  get #plan() {
    const planId = this.#planId;
    return this.#entry?.plans.find((plan) => String(plan.id) === planId);
  }

  #onChange = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.matches('[data-refill-mode]')) this.#render();
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-refill-plan]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#planIdValue = button.dataset.refillPlan || '';
    this.#render();
  };

  /** Arrow-key navigation, which role="radio" buttons have to provide. */
  #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-refill-plan]') : null;
    if (!(button instanceof HTMLElement)) return;
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const buttons = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-refill-plan]')]);
    const next = buttons[(buttons.indexOf(button) + step + buttons.length) % buttons.length];
    if (!next) return;
    this.#planIdValue = next.dataset.refillPlan || '';
    this.#render();
    next.focus();
  };

  #onVariantUpdate = (/** @type {any} */ event) => {
    const id = event?.detail?.resource?.id;
    if (id == null) return;
    this.#packVariantId = String(id);
    this.#render();
  };

  /**
   * Cadence fragment for the renewal line. Liquid derives it from the plan's
   * frequency option ("every month"); when that option is missing, fall back
   * to the plan name: "Delivery every 30 days" -> "every 30 days".
   * @param {Plan} plan
   */
  #cadence(plan) {
    if (plan.cadence) return plan.cadence;
    return plan.name.replace(/^\s*delivery\s+/i, '').trim() || plan.name;
  }

  /**
   * Rebuilds the frequency buttons, then syncs their checked state. The list is
   * only rebuilt when the plan ids actually change, so a re-render on every
   * keystroke does not blow away the element the shopper is tabbing through.
   * @param {Pair | undefined} entry
   */
  #renderFreqs(entry) {
    const host = this.querySelector('[data-refill-freqs]');
    if (!(host instanceof HTMLElement)) return;

    const plans = entry?.plans ?? [];
    const wanted = plans.map((plan) => String(plan.id)).join(',');
    if (host.dataset.planIds !== wanted) {
      host.dataset.planIds = wanted;
      host.replaceChildren(
        ...plans.map((plan) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('role', 'radio');
          button.dataset.refillPlan = String(plan.id);
          button.textContent = this.#freqLabel(plan);
          return button;
        })
      );
    }

    const selected = this.#planId;
    for (const button of host.querySelectorAll('[data-refill-plan]')) {
      if (!(button instanceof HTMLElement)) continue;
      const checked = button.dataset.refillPlan === selected;
      button.setAttribute('aria-checked', String(checked));
      // Roving tabindex: the group is one tab stop, arrows move within it.
      button.tabIndex = checked ? 0 : -1;
    }

    // One plan is not a choice — the shopper still gets it, just not a control
    // that implies there is something to decide.
    host.hidden = !this.#subscribed || plans.length < 2;
  }

  /**
   * Fills a list of token templates, hiding the rows that came back unfit.
   * @param {Iterable<Element>} items
   * @param {Plan | undefined} plan
   * @returns {number} how many rows survived
   */
  #fillList(items, plan) {
    let shown = 0;
    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      item.hidden = !this.#fillTpl(item, plan);
      if (!item.hidden) shown += 1;
    }
    return shown;
  }

  #render() {
    const entry = this.#entry;
    const usable = !!entry && entry.available && entry.plans.length > 0;

    const row = this.querySelector('[data-refill-subscribe-row]');
    if (row instanceof HTMLElement) row.hidden = !usable;

    // A pack with no placeholder must never leave "subscribe" selected behind.
    if (!usable) {
      const oneTime = this.querySelector('[data-refill-mode][value=""]');
      if (oneTime instanceof HTMLInputElement) oneTime.checked = true;
    }

    this.#renderFreqs(entry);

    const plan = this.#plan;
    const discounted = this.#discounted(plan);

    // One-time always quotes the pack's own list price.
    const onetimePrice = this.querySelector('[data-refill-onetime-price]');
    if (onetimePrice instanceof HTMLElement) onetimePrice.textContent = entry?.was ?? '';

    // Subscribing quotes the marked-down price when Liquid could verify it
    // against the Function, and the plain list price when it could not — in
    // which case both rows correctly show the same number.
    const today = this.querySelector('[data-refill-today]');
    if (today instanceof HTMLElement) today.textContent = (discounted ? plan?.today : entry?.was) ?? '';

    const contents = this.querySelector('[data-refill-contents]');
    if (contents instanceof HTMLElement) contents.hidden = !this.#fillTpl(contents, plan);

    // The badge is a savings claim, so it lives or dies by the same rule.
    const chip = this.querySelector('[data-refill-chip]');
    if (chip instanceof HTMLElement) chip.hidden = !this.#fillTpl(chip, plan);

    const renewal = this.querySelector('[data-refill-renewal]');
    if (renewal instanceof HTMLElement) {
      renewal.textContent = plan ? this.#fill(this.dataset.renewalTemplate || '{price} {cadence}', plan) : '';
    }

    const note = this.querySelector('[data-refill-note]');
    if (note instanceof HTMLElement) note.hidden = !this.#subscribed || !this.#fillTpl(note, plan);

    const benefits = this.querySelector('[data-refill-benefits]');
    if (benefits instanceof HTMLElement) {
      const shown = this.#fillList(benefits.children, plan);
      benefits.hidden = !this.#subscribed || !shown;
    }

    // The explainer stays readable whichever option is selected — a shopper
    // deciding needs the terms before choosing, not after. It goes only when
    // there is no subscription left to explain.
    const how = this.querySelector('.ct-refill__how');
    if (how instanceof HTMLElement) {
      const shown = this.#fillList(how.querySelectorAll('[data-refill-how] li'), plan);
      how.hidden = !usable || !shown;
    }

    const debug = this.querySelector('[data-refill-debug]');
    if (debug instanceof HTMLElement) {
      debug.textContent = JSON.stringify(
        {
          packVariantId: this.#packVariantId,
          matched: !!entry,
          usable,
          subscribed: this.#subscribed,
          planId: this.#planId,
          discounted,
          formFound: !!this.#form,
          pairs: this.#pairs,
        },
        null,
        2
      );
    }
  }

  // ---------------------------------------------------------------------- //

  #onSubmit = (/** @type {Event} */ event) => {
    if (!event.target || event.target !== this.#form) return;
    if (!this.#subscribed) return; // one-time: the theme's handler owns this

    const entry = this.#entry;
    const planId = this.#planId;
    if (!entry || !planId) return; // fail open to a plain one-time add

    event.preventDefault();
    event.stopPropagation();

    void this.#addPair(entry, planId);
  };

  /**
   * @param {Pair} entry
   * @param {string} planId
   */
  async #addPair(entry, planId) {
    const form = this.#form;
    if (!form) return;

    // Ties THIS pack line to THIS placeholder line. Without it the two are
    // matched by variant id alone, and a pack bought one-time is
    // indistinguishable from the same pack bought on subscription — so adding
    // a one-time pack dragged the subscription's quantity up with it, and a
    // second cadence for the same size overwrote the first. The token also
    // stops Shopify merging a subscribed pack line into a one-time one.
    const pairToken = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput instanceof HTMLInputElement ? quantityInput.value : 1) || 1);
    const lineNote = this.#lineNote(entry.plans.find((plan) => String(plan.id) === planId));

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    this.#setError('');
    this.#setBusy(true);

    try {
      // Shopify.routes.root carries the market/locale prefix (e.g. /en-gb/);
      // the theme's `Theme.routes` is a script-scoped const, not on window.
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(this.#packVariantId), quantity, properties: { [PAIR_KEY]: pairToken } },
            // Mirrors the pack quantity: the Function requires an unconsumed
            // matching pack for EVERY placeholder unit, or it zeroes none.
            {
              id: entry.id,
              quantity,
              selling_plan: Number(planId),
              properties: { ...(lineNote ?? {}), [PAIR_KEY]: pairToken },
            },
          ],
          ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
        }),
      });

      const body = await response.json();

      if (!response.ok || body?.status) {
        this.#setError(body?.description || body?.message || 'Could not add to cart.');
        return;
      }

      this.closest('.shopify-section')?.querySelector('add-to-cart-component')?.animateAddToCart?.();

      // cart-icon SETS the bubble from itemCount unless the source is
      // 'product-form-component' (which makes it add), so send the absolute
      // count — a two-line add would otherwise render as a cart of 1.
      let itemCount;
      try {
        itemCount = (await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json())
          ?.item_count;
      } catch {
        itemCount = undefined;
      }

      form.dispatchEvent(
        new CartAddEvent({}, this.#packVariantId, {
          source: 'ct-refill-plan-toggle',
          itemCount,
          variantId: this.#packVariantId,
          sections: body?.sections,
        })
      );

      refreshSmartCart();
    } catch (error) {
      console.error('[ct-refill-plan-toggle]', error);
      this.#setError('Could not add to cart. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string} message */
  #setError(message) {
    const element = this.querySelector('[data-refill-error]');
    if (!(element instanceof HTMLElement)) return;
    element.textContent = message;
    element.hidden = !message;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const button = this.#form?.querySelector('button[type="submit"], button[name="add"]');
    if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', String(busy));
  }
}

if (!customElements.get('ct-refill-plan-toggle')) {
  customElements.define('ct-refill-plan-toggle', CtRefillPlanToggle);
}
